import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { derivePendingComponent, normalizeMessage, parseCtaLink } from '../messages';
import { useChatHistory } from './useChatHistory';
import type { ChatApi } from '../client/client';
import type { ChatErrorCode } from '../client/types';
import type { PendingGenerativeUiComponent } from '../components/generative-ui';
import type {
    ChatImageAttachment,
    DisplayMessage,
    RawGenerativeUiComponent,
    ReportError,
} from '../messages';

/** The render-side detail of an image-upload message, carried alongside its wire `content`. */
export type LocalImageInfo = {
    attachments: ChatImageAttachment[];
    caption?: string;
};

/** No-op default so callers that do not care about generative-UI parse failures can omit it. */
const noopReportError: ReportError = () => undefined;

/** The one place a history row's id becomes its display key, so the two call sites below can't drift. */
function historyMessageKey(id: number): string {
    return `h-${String(id)}`;
}

/**
 * A message this client is holding, as opposed to one loaded from the server.
 *
 * `status` tracks the same message through its lifecycle rather than identifying a
 * different entry — a message is appended once, when the customer sends it, and then
 * settles. Nothing keys off `content`: customers repeat themselves constantly in a brief
 * ("yes", "no", "sounds good"), so content is not an identity.
 */
type LocalMessage = {
    key: string;
    role: 'user' | 'assistant';
    content: string;
    /** `pending`: in flight. `sent`: the server persisted it. `failed`: it never landed. */
    status: 'pending' | 'sent' | 'failed';
    /**
     * `typed`: the customer's own free text — never JSON-parsed, so a customer typing
     * something that happens to look like a component reply can never be misread as one.
     * `componentReply`: this client built `content` itself as a component's wire envelope
     * (see the composer's `onSubmit`), so normalizing it through the registry is safe and is
     * what lets the answered tile render immediately, without waiting for a history reload.
     */
    origin: 'typed' | 'componentReply';
    /**
     * The reply's raw `component`, kept unresolved. Anything derived from it (a CTA link's
     * URL and label) needs locale context, which only exists at render time.
     */
    component?: RawGenerativeUiComponent;
    /**
     * Present only for an image-upload message. `content` stays the exact wire string (the
     * JSON envelope) so a retry re-sends it verbatim; this carries the render-ready form so
     * the optimistic bubble shows thumbnails instead of raw JSON.
     */
    image?: LocalImageInfo;
};

export type UseChatMessagesResult = {
    loading: boolean;
    loadError: Error | null;
    /**
     * The load failure's contract discriminator, passed straight through from
     * `useChatHistory` — this hook does no classifying of its own. Kept alongside
     * `loadError` (diagnostics only) so a consumer several layers up (`useChat`, then
     * `ChatInterface`) has a route to it, since calling `useChatHistory` a second time
     * would fetch the session twice.
     */
    errorCode: ChatErrorCode | null;
    /** `useChatHistory`'s real reload, passed straight through — never re-implemented here. */
    reload: () => void;
    /** `useChatHistory`'s real exhaustion flag, passed straight through. */
    reloadBudgetExhausted: boolean;
    /** The whole thread in order: loaded history, then everything added since. */
    messages: DisplayMessage[];
    /** How many messages the server holds — the count the next send must claim. */
    serverMessageCount: number;
    /** The conversation's message cap, forwarded from history. Null only while loading/failed. */
    maxMessages: number | null;
    /** The most recent message that failed to send, for offering a retry. */
    lastFailed: { key: string; content: string } | null;
    /** Present once the session is complete. Undefined while still in progress. */
    completedAt: string | undefined;
    /**
     * The follow-up question from whichever message is currently last in the thread, for the
     * composer to render as an interactive selector — sourced from loaded history on mount and
     * from the settled reply on every send after. Not part of `messages`: an awaiting-selection
     * state is composer state, not thread history, even though (unlike `messages`) it does
     * survive a reload, because history hydration derives it too.
     */
    pendingComponent: PendingGenerativeUiComponent | null;
    /**
     * The one reply that should reveal word by word: the last message hydration found
     * when it loaded (only if that was the assistant's), or whichever reply the most
     * recent send settled since — never both, and never more than one.
     */
    latestReplyKey: string | null;
    /** Adds a composer-submitted component reply in flight. Returns its key. */
    addPendingComponentReply: (content: string) => string;
    /**
     * Adds the customer's message in flight. Returns its key. Pass `image` for an
     * image-upload message: `content` remains the wire string (for retry), while `image`
     * drives the optimistic thumbnail render.
     */
    addPending: (content: string, image?: LocalImageInfo) => string;
    /** The turn landed: promotes the sent message, appends the reply, drops orphans. */
    settleSent: (
        key: string,
        replyContent: string,
        messageCount: number,
        component?: RawGenerativeUiComponent,
        completedAt?: string
    ) => void;
    /** The turn did not land. The bubble stays; the count does not move. */
    settleFailed: (key: string) => void;
    /** Puts a failed message back in flight for a retry. */
    retryPending: (key: string) => void;
};

/**
 * Owns what is in the thread and how many messages the server has.
 *
 * Split out of `useChat` so the send lifecycle does not also have to do list
 * bookkeeping. The boundary is deliberate: this hook knows nothing about errors,
 * retries or timeouts, and `useChat` knows nothing about keys, ordering or how many
 * messages a turn persists.
 *
 * `serverMessageCount` comes from the service: every successful send reports the
 * conversation length it persisted, and that number is carried straight into the next
 * send. It is never derived from the rendered list, which holds things the server has
 * never seen — an in-flight message, a failed one, and in future generative-UI rows and
 * uploads. The pilot counts its message log and consequently has to filter component
 * rows, exclude the failed message from the payload, then delete it again after the next
 * success: three corrections undoing one bad premise.
 *
 * Taking the service's number also means this client holds no opinion about how many
 * rows a turn persists. It is two today; if that ever changes, the count follows without
 * a change here.
 */
export function useChatMessages(
    briefId: string,
    api: ChatApi,
    reportError: ReportError = noopReportError
): UseChatMessagesResult {
    const history = useChatHistory(briefId, api);
    const [localMessages, setLocalMessages] = useState<LocalMessage[]>([]);
    const [countFromLastTurn, setCountFromLastTurn] = useState<number | null>(null);
    const [completedAt, setCompletedAt] = useState<string | undefined>(undefined);
    const nextKey = useRef(0);

    // Latest-ref rather than a `useMemo` dependency: a caller passing an inline arrow
    // function hands this hook a new identity every render, and putting it in the
    // deps array below would re-run normalization on every render regardless of
    // whether the messages themselves changed.
    const reportErrorRef = useRef(reportError);
    reportErrorRef.current = reportError;

    // Minted outside the state updaters: React may invoke an updater more than once,
    // which would burn keys and, worse, hand two messages the same one.
    const mintKey = useCallback(() => `l-${String(nextKey.current++)}`, []);

    const [pendingComponent, setPendingComponent] = useState<PendingGenerativeUiComponent | null>(
        null
    );

    // History resolves once, asynchronously — this is the hydration half of the single
    // derivation path (the other half is `settleSent` below). Reading `component` off
    // the last loaded message is what lets a question the customer never answered still
    // render as an active selector after a reload, instead of only after the next send.
    useEffect(() => {
        if (history.loading || history.error !== null) {
            return;
        }

        const lastMessage = history.messages[history.messages.length - 1];
        setPendingComponent(derivePendingComponent(lastMessage));
    }, [history.loading, history.error, history.messages]);

    // Seeds completion from history so a reload after completion shows the completed
    // state immediately rather than waiting for a send. `?? prev` keeps it monotonic.
    useEffect(() => {
        if (history.loading || history.error !== null) {
            return;
        }

        setCompletedAt((prev) => prev ?? history.completedAt);
    }, [history.loading, history.error, history.completedAt]);

    // Not set via useEffect: MessageBubble freezes `animate` into state at mount, so
    // this must already be correct in the render where the last message first
    // appears — one render before any effect could run.
    const hydrationReplyKey = useMemo(() => {
        if (history.loading || history.error !== null) {
            return null;
        }

        const lastMessage = history.messages[history.messages.length - 1];
        return lastMessage !== undefined && lastMessage.role === 'assistant'
            ? historyMessageKey(lastMessage.id)
            : null;
    }, [history.loading, history.error, history.messages]);

    // A session reply is always the last entry `settleSent` appends below, so this
    // needs no state of its own — it just reads the list.
    const lastLocalMessage = localMessages[localMessages.length - 1];
    const sessionReplyKey =
        lastLocalMessage !== undefined && lastLocalMessage.role === 'assistant'
            ? lastLocalMessage.key
            : null;

    const latestReplyKey = sessionReplyKey ?? hydrationReplyKey;

    const appendPending = useCallback(
        (content: string, origin: LocalMessage['origin'], image?: LocalImageInfo) => {
            const key = mintKey();
            setLocalMessages((previous) => [
                ...previous,
                { key, role: 'user', content, status: 'pending', origin, image },
            ]);
            // A new message is going out — whatever question was pending is answered or
            // superseded either way, so it stops being offered in the composer immediately
            // rather than lingering until the response for this send comes back.
            setPendingComponent(null);
            return key;
        },
        [mintKey]
    );

    const addPending = useCallback(
        (content: string, image?: LocalImageInfo) => appendPending(content, 'typed', image),
        [appendPending]
    );

    const addPendingComponentReply = useCallback(
        (content: string) => appendPending(content, 'componentReply'),
        [appendPending]
    );

    const settleSent = useCallback(
        (
            key: string,
            replyContent: string,
            messageCount: number,
            component?: RawGenerativeUiComponent,
            newCompletedAt?: string
        ) => {
            const replyKey = mintKey();

            setLocalMessages((previous) => {
                // Drop any earlier failed message now that the conversation has moved
                // on. Left in place it would sit in the transcript forever, implying the
                // assistant simply ignored it. A message being retried is `pending`, not
                // `failed`, so it survives this.
                const settled = previous
                    .filter((message) => message.status !== 'failed')
                    .map((message) =>
                        message.key === key ? { ...message, status: 'sent' as const } : message
                    );

                return [
                    ...settled,
                    {
                        key: replyKey,
                        role: 'assistant',
                        content: replyContent,
                        status: 'sent',
                        origin: 'typed',
                        component,
                    },
                ];
            });

            // The service's own count, after the turn it just persisted. Set here and
            // nowhere else, so it cannot disagree with the append above.
            setCountFromLastTurn(messageCount);
            setPendingComponent(derivePendingComponent({ role: 'assistant', component }));
            // `?? prev` keeps completedAt monotonic once set.
            setCompletedAt((prev) => prev ?? newCompletedAt);
        },
        [mintKey]
    );

    const settleFailed = useCallback((key: string) => {
        setLocalMessages((previous) => withStatus(previous, key, 'failed'));
    }, []);

    const retryPending = useCallback((key: string) => {
        setLocalMessages((previous) => withStatus(previous, key, 'pending'));
    }, []);

    const lastFailed =
        [...localMessages].reverse().find((message) => message.status === 'failed') ?? null;

    const messages: DisplayMessage[] = useMemo(() => {
        const fromHistory = history.messages.map((message) =>
            // Prefixed by origin rather than status: history ids and local keys are
            // separate number spaces, and a status changes while an identity does not.
            normalizeMessage(message, historyMessageKey(message.id), reportErrorRef.current)
        );

        // Typed local messages are the customer's own words and are never JSON-parsed. A
        // componentReply is content this client built itself as a wire envelope, so it goes
        // through the same normalizer as a history row — that is what lets the answered tile
        // render immediately, rather than only after the next history reload. An image-upload
        // message carries its render form in `image`, so it becomes an imageUpload arm
        // directly rather than exposing its JSON envelope as text.
        const fromLocal: DisplayMessage[] = localMessages.map((message) =>
            message.image
                ? {
                      kind: 'imageUpload',
                      key: message.key,
                      role: message.role,
                      attachments: message.image.attachments,
                      caption: message.image.caption,
                  }
                : message.origin === 'componentReply'
                ? normalizeMessage(
                      { id: 0, role: message.role, content: message.content, createdAt: '' },
                      message.key,
                      reportErrorRef.current
                  )
                : {
                      kind: 'text',
                      key: message.key,
                      role: message.role,
                      content: message.content,
                      ctaLink: parseCtaLink(message.component),
                  }
        );

        return [...fromHistory, ...fromLocal];
    }, [history.messages, localMessages]);

    return {
        loading: history.loading,
        loadError: history.error,
        errorCode: history.errorCode,
        reload: history.reload,
        reloadBudgetExhausted: history.reloadBudgetExhausted,
        messages,
        // Until a turn has been sent, the loaded history is the only evidence of what
        // the server holds.
        serverMessageCount: countFromLastTurn ?? history.messages.length,
        maxMessages: history.maxMessages,
        lastFailed:
            lastFailed === null ? null : { key: lastFailed.key, content: lastFailed.content },
        completedAt,
        pendingComponent,
        latestReplyKey,
        addPending,
        addPendingComponentReply,
        settleSent,
        settleFailed,
        retryPending,
    };
}

function withStatus(
    messages: LocalMessage[],
    key: string,
    status: LocalMessage['status']
): LocalMessage[] {
    return messages.map((message) => (message.key === key ? { ...message, status } : message));
}
